import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {makeAnalyticsEventId} from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessInteractiveFlowchart} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {TooltipWrapper, TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, RED_DARK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import AvailableTaskSchemasPane from './AvailableTaskSchemasPane';
import {
  AvailableTaskSchema,
  ClientSchemaMapperConfig,
  isPlaceholderTaskSchema,
} from './FlowchartConfigConstants';
import {FlowchartConfigValidationResults} from './getFlowchartConfigValidationResults';
import SchemaMapperConfigsPane from './SchemaMapperConfigsPane';
import TemplateFlowchartPane from './TemplateFlowchartPane';

export enum FlowchartConfigTab {
  AVAILABLE_TASK_SCHEMAS = 'AVAILABLE_TASK_SCHEMAS',
  SCHEMA_MAPPER_CONFIGS = 'SCHEMA_MAPPER_CONFIGS',
  TEMPLATE_FLOWCHART = 'TEMPLATE_FLOWCHART',
}

enum NavigationMode {
  VIEW = 'VIEW',
  EDIT = 'EDIT',
}

export const TEMPLATE_FLOWCHART_ERROR_TEXT = 'There are some errors in this template flowchart.';
export const TEMPLATE_FLOWCHART_ERROR_SUPPORTING_INFO =
  'Edit the template flowchart to fix these errors so it can be used to create tasks.';

export interface GraphBuilderFlowchartConfigTabContainerProps {
  isEditing: boolean;
  availableTaskSchemas: Array<AvailableTaskSchema>;
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
  templateFlowchart: ProcessInteractiveFlowchart | null;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  allTaskSchemas: Array<ProcessTaskSchemaGraphQL>;
  allSchemasById: SchemaById;
  validationResults: FlowchartConfigValidationResults | null;
  didInitialFlowchartHaveChainedRouters: boolean;
  onChangeAvailableTaskSchemas: (availableTaskSchemas: Array<AvailableTaskSchema>) => void;
  onChangeSchemaMapperConfigs: (schemaMapperConfigs: Array<ClientSchemaMapperConfig>) => void;
  onChangeIsFlowchartModifiableOnCreation: (isFlowchartModifiableOnCreation: boolean) => void;
  onChangeIsFlowchartModifiableAfterCreation: (isFlowchartModifiableAfterCreation: boolean) => void;
  onChangeTemplateFlowchart: (templateFlowchart: ProcessInteractiveFlowchart | null) => void;
  onEnableEditing: () => void;
  onCancel: () => void;
  onSubmit: (configEditorEventGroupId: string) => void;
}

interface GraphBuilderFlowchartConfigHeaderProps {
  isEditing: boolean;
  submitDisabledMessage: string | undefined;
  onEnableEditing: () => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function GraphBuilderFlowchartConfigHeader(props: GraphBuilderFlowchartConfigHeaderProps): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-header', css(styles.selectItemContainer))}>
      <div className="simpleDivTable-headerCell simpleDivTable-header--title">Flowchart configuration</div>
      <div className={css(styles.selectItemContainer)}>
        {!props.isEditing ? (
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MINI}
            onClick={() => {
              props.onEnableEditing();
            }}
            icon="edit"
            text="Edit"
            dataTestAttrs={{
              component: 'GraphBuilderFlowchartConfigHeader',
              element: 'editButton',
            }}
          />
        ) : (
          <>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MINI}
              onClick={props.onCancel}
              text="Cancel"
              className={css(styles.buttonContainer)}
              dataTestAttrs={{
                component: 'GraphBuilderFlowchartConfigHeader',
                element: 'cancelButton',
              }}
            />
            <TooltipWrapper
              tooltipTitle={props.submitDisabledMessage}
              tooltipPlacement={TooltipPlacement.AUTO}
            >
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                size={ButtonSize.MINI}
                onClick={props.onSubmit}
                text="Submit"
                disabled={props.submitDisabledMessage != null}
                className={css(styles.buttonContainer)}
                dataTestAttrs={{
                  component: 'GraphBuilderFlowchartConfigHeader',
                  element: 'submitButton',
                }}
              />
            </TooltipWrapper>
          </>
        )}
      </div>
    </div>
  );
}

export default function GraphBuilderFlowchartConfigTabContainer(
  props: GraphBuilderFlowchartConfigTabContainerProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<FlowchartConfigTab>(FlowchartConfigTab.AVAILABLE_TASK_SCHEMAS);
  const [eventGroupId, setEventGroupId] = useState(makeAnalyticsEventId());

  return (
    <div
      className={css(styles.editorContainer)}
      {...getTestProps({component: 'GraphBuilderFlowchartConfigEditor', element: 'root'})}
    >
      <GraphBuilderFlowchartConfigHeader
        isEditing={props.isEditing}
        submitDisabledMessage={props.validationResults?.message}
        onSubmit={() => {
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.SUBMITTED_FLOWCHART_CONFIG_EDITOR, {
            process_client_origin: props.rootTaskSchema.clientOrigin,
            event_group_id: eventGroupId,
            action_origin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE_FLOWCHART_CONFIGURATION,
            active_tab: activeTab,
          });
          props.onSubmit(eventGroupId);
        }}
        onCancel={() => {
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLOSED_FLOWCHART_CONFIG_EDITOR, {
            process_client_origin: props.rootTaskSchema.clientOrigin,
            event_group_id: eventGroupId,
            action_origin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE_FLOWCHART_CONFIGURATION,
            active_tab: activeTab,
          });
          props.onCancel();
        }}
        onEnableEditing={() => {
          setEventGroupId(makeAnalyticsEventId()); // Set a new session uuid every time editing is enabled
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPENED_FLOWCHART_CONFIG_EDITOR, {
            event_group_id: eventGroupId,
            action_origin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE_FLOWCHART_CONFIGURATION,
            active_tab: activeTab,
            process_client_origin: props.rootTaskSchema.clientOrigin,
          });
          props.onEnableEditing();
        }}
      />
      <div className={css(styles.divider)} />
      <TabContainer<FlowchartConfigTab>
        activeTab={activeTab}
        onRequestTabChange={(newTab) => {
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_FLOWCHART_CONFIG_TAB, {
            process_client_origin: props.rootTaskSchema.clientOrigin,
            event_group_id: props.isEditing ? eventGroupId : null,
            action_origin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE_FLOWCHART_CONFIGURATION,
            tab_clicked: newTab,
            navigation_mode_when_clicked: props.isEditing ? NavigationMode.EDIT : NavigationMode.VIEW,
          });
          setActiveTab(newTab);
        }}
        className={classNames('u-flex-grow', css(styles.tabContainer))}
      >
        <Tab tabKey={FlowchartConfigTab.AVAILABLE_TASK_SCHEMAS} title="Task schemas">
          <AvailableTaskSchemasPane
            isEditing={props.isEditing}
            availableTaskSchemas={props.availableTaskSchemas}
            allTaskSchemas={props.allTaskSchemas}
            onChange={props.onChangeAvailableTaskSchemas}
          />
        </Tab>
        <Tab tabKey={FlowchartConfigTab.SCHEMA_MAPPER_CONFIGS} title="Transitions">
          <SchemaMapperConfigsPane
            isEditing={props.isEditing}
            schemaMapperConfigs={props.schemaMapperConfigs}
            availableTaskSchemas={
              props.availableTaskSchemas.filter(
                (availableTaskSchema) => !isPlaceholderTaskSchema(availableTaskSchema)
              ) as Array<ProcessTaskSchemaGraphQL>
            }
            allSchemasById={props.allSchemasById}
            rootTaskSchema={props.rootTaskSchema}
            onChange={props.onChangeSchemaMapperConfigs}
          />
        </Tab>
        <Tab
          tabKey={FlowchartConfigTab.TEMPLATE_FLOWCHART}
          title={
            props.validationResults?.tab === FlowchartConfigTab.TEMPLATE_FLOWCHART ? (
              <>
                <BenchlingIcon icon="revision" className={css(styles.tabIcon)} /> Template flowchart
              </>
            ) : (
              'Template flowchart'
            )
          }
        >
          {props.validationResults?.tab === FlowchartConfigTab.TEMPLATE_FLOWCHART && (
            <div className={css(styles.bannerWrapper)}>
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text={TEMPLATE_FLOWCHART_ERROR_TEXT}
                supportingInfo={TEMPLATE_FLOWCHART_ERROR_SUPPORTING_INFO}
              />
            </div>
          )}
          <TemplateFlowchartPane
            isEditing={props.isEditing}
            isFlowchartModifiableOnCreation={props.isFlowchartModifiableOnCreation}
            isFlowchartModifiableAfterCreation={props.isFlowchartModifiableAfterCreation}
            templateFlowchart={props.templateFlowchart}
            availableTaskSchemas={props.availableTaskSchemas}
            schemaMapperConfigs={props.schemaMapperConfigs}
            rootTaskSchema={props.rootTaskSchema}
            didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
            onChangeTemplateFlowchart={props.onChangeTemplateFlowchart}
            onToggleIsFlowchartModifiableOnCreation={props.onChangeIsFlowchartModifiableOnCreation}
            onToggleIsFlowchartModifiableAfterCreation={props.onChangeIsFlowchartModifiableAfterCreation}
            eventGroupId={eventGroupId}
          />
        </Tab>
      </TabContainer>
    </div>
  );
}

const styles = StyleSheet.create({
  editorContainer: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
    position: 'relative',
  },
  divider: {
    height: '1px',
    width: '100%',
    marginTop: '30px',
    left: '0px',
    background: `${GRAY_LIGHT}`,
    position: 'absolute',
  },
  tabContainer: {
    padding: '8px 14px',
  },
  selectItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  buttonContainer: {
    marginLeft: '15px',
  },
  bannerWrapper: {
    margin: '16px',
  },
  tabIcon: {
    color: RED_DARK,
    marginRight: '2px',
    transform: 'scale(1.2)',
  },
});
